import React, { useState, useMemo } from 'react';
import { Check, X, Zap, Star, Crown, Loader2, ArrowLeft, Sparkles, FileDown } from 'lucide-react';
import { buildPlanTools } from '@/utils/planTools';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const PricingPage = ({ onBack, onLogin, user }) => {
  const [loading, setLoading] = useState(null);
  const [error, setError] = useState(null);

  const planTools = useMemo(() => buildPlanTools(), []);

  const plans = [
    {
      id: 'free',
      name: 'Gratis',
      price: 0,
      priceLabel: 'Gratis',
      icon: Zap,
      color: 'from-slate-600 to-slate-700',
      borderColor: 'border-slate-600',
      target: 'Para explorar la plataforma',
      highlights: [
        '3 activos máximo',
        'Datos en tiempo real',
        'Gráfico de líneas',
      ],
      tools: planTools.free,
      cta: user ? 'Plan Actual' : 'Empezar Gratis',
      disabled: user?.plan === 'free',
    },
    {
      id: 'pro',
      name: 'Pro',
      price: 19.99,
      priceLabel: '€19.99',
      oneTime: true,
      icon: Star,
      color: 'from-gold-500 to-zinc-600',
      borderColor: 'border-gold-500',
      popular: true,
      target: 'Ideal para principiantes',
      highlights: [
        '15 activos',
        'Gráficos de velas completos',
        'Alertas de precio · Exportar PDF',
      ],
      tools: planTools.pro,
      cta: user?.plan === 'pro' || user?.plan === 'basic' ? 'Plan Actual' : 'Comprar Pro',
      disabled: user?.plan === 'pro' || user?.plan === 'basic' || user?.plan === 'ultimate' || user?.plan === 'premium',
    },
    {
      id: 'ultimate',
      name: 'Ultimate',
      price: 29.99,
      priceLabel: '€29.99',
      oneTime: true,
      icon: Crown,
      color: 'from-gold-500 to-orange-600',
      borderColor: 'border-gold-500',
      target: 'Para traders con experiencia',
      highlights: [
        'Activos ilimitados',
        'Todo lo del plan Pro',
        'Herramientas Ultimate exclusivas',
      ],
      tools: planTools.ultimate,
      cta: user?.plan === 'ultimate' || user?.plan === 'premium' ? 'Plan Actual' : 'Comprar Ultimate',
      disabled: user?.plan === 'ultimate' || user?.plan === 'premium',
    },
  ];

  const handlePurchase = async (planId) => {
    if (!user) {
      onLogin?.();
      return;
    }

    if (planId === 'free') return;

    setLoading(planId);
    setError(null);

    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      if (!token) {
        setError('No has iniciado sesión');
        setLoading(null);
        return;
      }

      const response = await fetch(`${API_URL}/api/payments/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        credentials: 'include',
        body: JSON.stringify({
          plan_id: planId,
          origin_url: window.location.origin,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Error al procesar el pago');
      }

      window.location.href = data.url;
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(null);
    }
  };

  // ---------------------------------------------------------------------------
  // Descarga PDF con las herramientas de cada plan
  // ---------------------------------------------------------------------------
  const handleDownloadPDF = () => {
    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();

    // Portada
    doc.setFillColor(10, 22, 40);
    doc.rect(0, 0, pageWidth, 120, 'F');
    doc.setTextColor(255, 214, 0);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(26);
    doc.text('KAPITAL · Herramientas por plan', pageWidth / 2, 55, { align: 'center' });
    doc.setTextColor(180, 200, 220);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.text('Comparativa completa · Gratis · Pro (€19.99) · Ultimate (€29.99)', pageWidth / 2, 82, { align: 'center' });
    doc.setFontSize(9);
    doc.text(`Generado el ${new Date().toLocaleString('es-ES', { timeZone: 'Europe/Madrid' })}`, pageWidth / 2, 100, { align: 'center' });

    // Resumen por plan
    const summaryRows = plans.map(p => [
      p.name,
      p.priceLabel + (p.oneTime ? ' (pago único)' : ''),
      `${p.tools.length} herramientas`,
      p.target,
    ]);
    autoTable(doc, {
      startY: 140,
      head: [['Plan', 'Precio', 'Total', 'Recomendado para']],
      body: summaryRows,
      theme: 'grid',
      headStyles: { fillColor: [15, 118, 176], textColor: 255, fontStyle: 'bold' },
      styles: { fontSize: 10, cellPadding: 6 },
      alternateRowStyles: { fillColor: [246, 250, 253] },
    });

    // Una tabla por plan
    plans.forEach((p, i) => {
      doc.addPage();
      // Cabecera del plan
      const headerColor = p.id === 'free' ? [100, 116, 139] : p.id === 'pro' ? [6, 182, 212] : [245, 158, 11];
      doc.setFillColor(...headerColor);
      doc.rect(0, 0, pageWidth, 70, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(22);
      doc.text(`Plan ${p.name} — ${p.priceLabel}`, 40, 35);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11);
      doc.text(`${p.target} · ${p.tools.length} herramientas incluidas`, 40, 55);

      const rows = p.tools.map((t, idx) => [
        String(idx + 1),
        t.name,
        t.description || '—',
      ]);
      autoTable(doc, {
        startY: 90,
        head: [['#', 'Herramienta', 'Descripción']],
        body: rows,
        theme: 'grid',
        headStyles: { fillColor: headerColor, textColor: 255, fontStyle: 'bold' },
        styles: { fontSize: 9.5, cellPadding: 5 },
        alternateRowStyles: { fillColor: [246, 250, 253] },
        columnStyles: {
          0: { cellWidth: 30, halign: 'center' },
          1: { cellWidth: 180, fontStyle: 'bold' },
        },
      });
    });

    doc.save(`KAPITAL_planes_herramientas_${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  return (
    <div className="min-h-screen py-12 px-4" style={{ background: 'linear-gradient(135deg, #0a1628 0%, #1a2744 100%)' }}>
      <div className="max-w-7xl mx-auto">
        {onBack && (
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-slate-400 hover:text-white mb-8 transition-colors"
            data-testid="pricing-back-btn"
          >
            <ArrowLeft size={18} />
            <span>Volver</span>
          </button>
        )}

        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-white mb-4">Elige tu plan</h1>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto">
            Pago único, acceso de por vida. Sin suscripciones, sin sorpresas.
          </p>

          <div className="flex flex-wrap items-center justify-center gap-3 mt-5">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/30">
              <Sparkles size={16} className="text-green-400" />
              <span className="text-green-400 font-medium text-sm">Pago único · Sin suscripción mensual</span>
            </div>
            <button
              onClick={handleDownloadPDF}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-gold-500/20 to-orange-500/20 border border-gold-500/40 text-gold-300 hover:bg-zinc-800/70 hover:border-gold-400 transition-all"
              data-testid="pricing-download-pdf-btn"
              title="Descarga un PDF con TODAS las herramientas de cada plan"
            >
              <FileDown size={16} strokeWidth={2.5} />
              <span className="font-medium text-sm">Descargar comparativa (PDF)</span>
            </button>
          </div>
        </div>

        {error && (
          <div className="max-w-md mx-auto mb-8 bg-red-500/10 border border-red-500/30 rounded-lg p-4">
            <p className="text-red-400 text-center">{error}</p>
          </div>
        )}

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {plans.map((plan) => {
            const IconComponent = plan.icon;
            const isCurrentPlan = user?.plan === plan.id ||
              (plan.id === 'pro' && user?.plan === 'basic') ||
              (plan.id === 'ultimate' && user?.plan === 'premium');

            return (
              <div
                key={plan.id}
                className={`relative rounded-2xl p-6 lg:p-7 transition-all duration-300 flex flex-col ${
                  plan.popular ? 'ring-2 ring-gold-500' : ''
                }`}
                style={{
                  background: 'linear-gradient(165deg, rgba(15, 25, 45, 0.9) 0%, rgba(10, 18, 35, 0.95) 100%)',
                  border: `1px solid ${plan.popular ? 'rgba(6, 182, 212, 0.5)' : 'rgba(100, 116, 139, 0.3)'}`,
                  boxShadow: plan.popular
                    ? '0 20px 40px rgba(0, 0, 0, 0.4), 0 0 30px rgba(6, 182, 212, 0.1)'
                    : '0 10px 30px rgba(0, 0, 0, 0.3)',
                }}
                data-testid={`pricing-card-${plan.id}`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-gradient-to-r from-gold-500 to-zinc-600 rounded-full">
                    <span className="text-white text-xs font-bold">MÁS POPULAR</span>
                  </div>
                )}

                <div className="text-center mb-5">
                  <div className={`inline-flex p-3 rounded-xl bg-gradient-to-br ${plan.color} mb-3`}>
                    <IconComponent size={28} className="text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-1">{plan.name}</h3>
                  <p className="text-slate-400 text-sm">{plan.target}</p>
                </div>

                <div className="text-center mb-5">
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-4xl font-bold text-white">{plan.priceLabel}</span>
                  </div>
                  {plan.oneTime && (
                    <p className="text-green-400 text-sm mt-1 font-medium">Pago único · Para siempre</p>
                  )}
                  {plan.id === 'free' && (
                    <p className="text-slate-500 text-sm mt-1">Sin tarjeta de crédito</p>
                  )}
                </div>

                {/* Highlights */}
                <ul className="space-y-2 mb-4">
                  {plan.highlights.map((h, idx) => (
                    <li key={idx} className="flex items-center gap-2">
                      <Check size={16} className="text-green-400 shrink-0" />
                      <span className="text-slate-300 text-sm font-medium">{h}</span>
                    </li>
                  ))}
                </ul>

                {/* Lista completa de herramientas */}
                <div
                  className="mb-6 rounded-lg border border-slate-700/60 bg-slate-900/40 p-3"
                  data-testid={`pricing-tools-${plan.id}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">
                      Herramientas incluidas
                    </span>
                    <span className="text-[11px] font-mono text-gold-300 font-bold">
                      {plan.tools.length}
                    </span>
                  </div>
                  <ul className="space-y-1.5 max-h-72 overflow-y-auto pr-1 kapital-scroll">
                    {plan.tools.map((t) => (
                      <li key={t.id} className="flex items-start gap-2 text-[12.5px] leading-snug">
                        <Check size={13} className="text-green-400 shrink-0 mt-[3px]" strokeWidth={3} />
                        <span className="text-slate-200">
                          <span className="font-medium">{t.name}</span>
                          {t.description && (
                            <span className="block text-[11px] text-slate-500">{t.description}</span>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* CTA */}
                <button
                  onClick={() => handlePurchase(plan.id)}
                  disabled={plan.disabled || loading === plan.id}
                  className={`mt-auto w-full py-3 px-4 rounded-xl font-medium transition-all duration-200 flex items-center justify-center gap-2
                    ${isCurrentPlan
                      ? 'bg-slate-700 text-slate-400 cursor-default'
                      : plan.disabled
                        ? 'bg-slate-800 text-slate-500 cursor-not-allowed'
                        : plan.popular
                          ? 'bg-gradient-to-r from-gold-500 to-zinc-600 text-white hover:shadow-lg hover:shadow-gold-500/25'
                          : plan.id === 'ultimate'
                            ? 'bg-gradient-to-r from-gold-500 to-orange-600 text-white hover:shadow-lg hover:shadow-gold-500/25'
                            : 'bg-slate-700 text-white hover:bg-slate-600'
                    }`}
                  data-testid={`pricing-cta-${plan.id}`}
                >
                  {loading === plan.id ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      <span>Procesando...</span>
                    </>
                  ) : (
                    <span>{plan.cta}</span>
                  )}
                </button>

                {isCurrentPlan && (
                  <p className="text-center text-green-400 text-sm mt-3 font-medium">✓ Tu plan actual</p>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-12 text-center">
          <div className="inline-flex flex-wrap justify-center gap-6 text-slate-500 text-sm">
            <span className="flex items-center gap-2">
              <Check size={16} className="text-green-400" /> Pago seguro con Stripe
            </span>
            <span className="flex items-center gap-2">
              <Check size={16} className="text-green-400" /> Sin renovación automática
            </span>
            <span className="flex items-center gap-2">
              <Check size={16} className="text-green-400" /> Acceso inmediato
            </span>
          </div>
        </div>

        <div className="mt-16 max-w-2xl mx-auto">
          <h2 className="text-2xl font-bold text-white text-center mb-8">Preguntas Frecuentes</h2>
          <div className="space-y-4">
            <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700">
              <h3 className="text-white font-medium mb-2">¿Es realmente un pago único?</h3>
              <p className="text-slate-400 text-sm">Sí. Pagas una vez y tienes acceso de por vida a todas las funciones de tu plan. No hay cargos mensuales ni renovaciones automáticas.</p>
            </div>
            <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700">
              <h3 className="text-white font-medium mb-2">¿Puedo actualizar mi plan después?</h3>
              <p className="text-slate-400 text-sm">¡Por supuesto! Si tienes el plan Pro y quieres Ultimate, solo pagarás la diferencia (€30). El sistema calcula automáticamente el descuento.</p>
            </div>
            <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700">
              <h3 className="text-white font-medium mb-2">¿Los datos son en tiempo real?</h3>
              <p className="text-slate-400 text-sm">Sí. Utilizamos datos de mercado actualizados. Ten en cuenta que algunos mercados pueden tener un retraso de 15-20 minutos según el proveedor.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
